// src/pages/ManageCarList.js
import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../components/Toast';
import { useConfirm } from '../components/ConfirmModal';
import { colors } from '../theme/tokens';

const ManageCarList = () => {
  const { token, canApprove } = useAuth();
  const navigate = useNavigate();
  const showToast = useToast();
  const confirm = useConfirm();
  const [carList, setCarList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionLoading, setActionLoading] = useState({});
  const [searchTerm, setSearchTerm] = useState('');
  const [showAddForm, setShowAddForm] = useState(false);
  const [newCar, setNewCar] = useState({ make: '', model: '' });
  const [editingId, setEditingId] = useState(null);
  const [editForm, setEditForm] = useState({ make: '', model: '' });

  const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

  useEffect(() => {
    if (!canApprove) {
      navigate('/');
      return;
    }
    fetchCarList();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canApprove, navigate]);

  const fetchCarList = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API_URL}/api/car-list/all`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCarList(response.data);
    } catch (error) {
      showToast('Failed to load car list: ' + (error.response?.data?.error || 'Unknown error'), 'error');
    } finally {
      setLoading(false);
    }
  };

  const handleAdd = async (e) => {
    e.preventDefault();
    if (!newCar.make || !newCar.model) {
      showToast('Please fill in both make and model', 'warning');
      return;
    }

    setActionLoading(prev => ({ ...prev, add: true }));
    try {
      await axios.post(`${API_URL}/api/car-list`, newCar, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Make/model added successfully!', 'success');
      setNewCar({ make: '', model: '' });
      setShowAddForm(false);
      fetchCarList();
    } catch (error) {
      showToast('Failed to add: ' + (error.response?.data?.error || 'Unknown error'), 'error');
    } finally {
      setActionLoading(prev => ({ ...prev, add: false }));
    }
  };

  const handleEdit = async (id) => {
    if (!editForm.make || !editForm.model) {
      showToast('Please fill in both make and model', 'warning');
      return;
    }

    setActionLoading(prev => ({ ...prev, [`edit-${id}`]: true }));
    try {
      await axios.put(`${API_URL}/api/car-list/${id}`, editForm, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Make/model updated successfully!', 'success');
      setEditingId(null);
      fetchCarList();
    } catch (error) {
      showToast('Failed to update: ' + (error.response?.data?.error || 'Unknown error'), 'error');
    } finally {
      setActionLoading(prev => ({ ...prev, [`edit-${id}`]: false }));
    }
  };

  const handleDelete = async (id, make, model) => {
    const ok = await confirm(`Delete ${make} ${model}? This cannot be undone.`, {
      title: 'Delete Make/Model',
      icon: '🗑️',
      confirmText: 'Delete',
      danger: true
    });
    if (!ok) return;

    setActionLoading(prev => ({ ...prev, [`del-${id}`]: true }));
    try {
      await axios.delete(`${API_URL}/api/car-list/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      showToast('Make/model deleted successfully!', 'success');
      fetchCarList();
    } catch (error) {
      showToast('Failed to delete: ' + (error.response?.data?.error || 'Unknown error'), 'error');
    } finally {
      setActionLoading(prev => ({ ...prev, [`del-${id}`]: false }));
    }
  };

  const startEdit = (car) => {
    setEditingId(car._id);
    setEditForm({ make: car.make, model: car.model });
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditForm({ make: '', model: '' });
  };

  const filteredList = carList.filter(car => 
    car.make.toLowerCase().includes(searchTerm.toLowerCase()) ||
    car.model.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const makeStats = carList.reduce((acc, car) => {
    acc[car.make] = (acc[car.make] || 0) + 1;
    return acc;
  }, {});

  const uniqueMakes = Object.keys(makeStats).length;
  const totalCombinations = carList.length;

  if (loading) {
    return <div style={styles.loading}>Loading car list...</div>;
  }

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h1 style={styles.title}>🚗 Manage Car Make & Model List</h1>
        <p style={styles.subtitle}>Add, edit, or remove car makes and models from the database</p>
      </div>

      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{uniqueMakes}</div>
          <div style={styles.statLabel}>Unique Makes</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{totalCombinations}</div>
          <div style={styles.statLabel}>Total Combinations</div>
        </div>
        <div style={styles.statCard}>
          <div style={styles.statNumber}>{filteredList.length}</div>
          <div style={styles.statLabel}>Filtered Results</div>
        </div>
      </div>

      <div style={styles.toolbar}>
        <button 
          onClick={() => setShowAddForm(!showAddForm)} 
          style={styles.addBtn}
        >
          {showAddForm ? '✗ Cancel' : '+ Add New Make/Model'}
        </button>
        <input
          type="text"
          placeholder="Search makes or models..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          style={styles.searchInput}
        />
      </div>

      {showAddForm && (
        <div style={styles.addForm}>
          <h3 style={styles.formTitle}>Add New Make-Model Combination</h3>
          <form onSubmit={handleAdd} style={styles.form}>
            <input
              type="text"
              placeholder="Make (e.g., Toyota)"
              value={newCar.make}
              onChange={(e) => setNewCar({ ...newCar, make: e.target.value })}
              style={styles.input}
              required
            />
            <input
              type="text"
              placeholder="Model (e.g., Corolla)"
              value={newCar.model}
              onChange={(e) => setNewCar({ ...newCar, model: e.target.value })}
              style={styles.input}
              required
            />
            <button
              type="submit"
              style={{...styles.submitBtn, opacity: actionLoading.add ? 0.6 : 1}}
              disabled={actionLoading.add}
            >
              {actionLoading.add ? 'Adding...' : 'Add'}
            </button>
          </form>
        </div>
      )}

      <div style={styles.tableContainer}>
        <table style={styles.table}>
          <thead>
            <tr>
              <th style={styles.th}>Make</th>
              <th style={styles.th}>Model</th>
              <th style={styles.th}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredList.map(car => (
              <tr key={car._id} style={styles.tr}>
                {editingId === car._id ? (
                  <>
                    <td style={styles.td}>
                      <input
                        type="text"
                        value={editForm.make}
                        onChange={(e) => setEditForm({ ...editForm, make: e.target.value })}
                        style={styles.editInput}
                      />
                    </td>
                    <td style={styles.td}>
                      <input
                        type="text"
                        value={editForm.model}
                        onChange={(e) => setEditForm({ ...editForm, model: e.target.value })}
                        style={styles.editInput}
                      />
                    </td>
                    <td style={styles.td}>
                      <button 
                        onClick={() => handleEdit(car._id)} 
                        disabled={actionLoading[`edit-${car._id}`]}
                        style={{...styles.actionBtn, ...styles.saveBtn, opacity: actionLoading[`edit-${car._id}`] ? 0.6 : 1}}
                      >
                        {actionLoading[`edit-${car._id}`] ? '...' : '✓ Save'}
                      </button>
                      <button 
                        onClick={cancelEdit} 
                        style={{...styles.actionBtn, ...styles.cancelBtn}}
                      >
                        ✗ Cancel
                      </button>
                    </td>
                  </>
                ) : (
                  <>
                    <td style={styles.td}>{car.make}</td>
                    <td style={styles.td}>{car.model}</td>
                    <td style={styles.td}>
                      <button 
                        onClick={() => startEdit(car)} 
                        style={{...styles.actionBtn, ...styles.editBtn}}
                      >
                        ✎ Edit
                      </button>
                      <button 
                        onClick={() => handleDelete(car._id, car.make, car.model)} 
                        disabled={actionLoading[`del-${car._id}`]}
                        style={{...styles.actionBtn, ...styles.deleteBtn, opacity: actionLoading[`del-${car._id}`] ? 0.6 : 1}}
                      >
                        {actionLoading[`del-${car._id}`] ? '...' : '🗑 Delete'}
                      </button>
                    </td>
                  </>
                )}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {filteredList.length === 0 && (
        <div style={styles.emptyState}>
          No results found {searchTerm && `for "${searchTerm}"`}
        </div>
      )}
    </div>
  );
};

const styles = {
  container: { maxWidth: '1200px', margin: '20px auto', padding: '20px' },
  loading: { textAlign: 'center', padding: '50px', fontSize: '18px', color: colors.text },
  header: { textAlign: 'center', marginBottom: '30px' },
  title: { color: colors.text, marginBottom: '10px' },
  subtitle: { color: colors.textMuted, fontSize: '14px' },
  statsGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '20px', marginBottom: '30px' },
  statCard: { background: colors.surface, padding: '20px', borderRadius: '10px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', textAlign: 'center', border: `1px solid ${colors.border}` },
  statNumber: { fontSize: '36px', fontWeight: 'bold', color: colors.text },
  statLabel: { fontSize: '14px', color: colors.textMuted, marginTop: '5px' },
  toolbar: { display: 'flex', gap: '15px', marginBottom: '20px', flexWrap: 'wrap' },
  addBtn: { padding: '12px 24px', background: colors.parGreen, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  searchInput: { flex: 1, minWidth: '200px', padding: '12px', border: `1px solid ${colors.border}`, borderRadius: '8px', fontSize: '14px', background: colors.inputBg, color: colors.text },
  addForm: { background: colors.surface, color: colors.text, padding: '25px', borderRadius: '10px', marginBottom: '20px', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', border: `1px solid ${colors.border}` },
  formTitle: { marginBottom: '15px', color: colors.text },
  form: { display: 'grid', gridTemplateColumns: '1fr 1fr auto', gap: '10px' },
  input: { padding: '12px', border: `1px solid ${colors.border}`, borderRadius: '8px', fontSize: '14px', background: colors.inputBg, color: colors.text },
  submitBtn: { padding: '12px 24px', background: colors.parGreen, color: 'white', border: 'none', borderRadius: '8px', cursor: 'pointer', fontSize: '14px', fontWeight: '600' },
  tableContainer: { background: colors.surface, color: colors.text, borderRadius: '10px', overflow: 'hidden', boxShadow: '0 2px 10px rgba(0,0,0,0.1)', border: `1px solid ${colors.border}` },
  table: { width: '100%', borderCollapse: 'collapse' },
  th: { textAlign: 'left', padding: '15px', borderBottom: `2px solid ${colors.border}`, fontWeight: '600', color: colors.text, background: colors.surfaceSoft },
  tr: { borderBottom: `1px solid ${colors.borderSoft}` },
  td: { padding: '12px 15px' },
  editInput: { width: '100%', padding: '8px', border: `1px solid ${colors.border}`, borderRadius: '5px', background: colors.inputBg, color: colors.text },
  actionBtn: { padding: '6px 12px', border: 'none', borderRadius: '5px', cursor: 'pointer', fontSize: '12px', fontWeight: '600', marginRight: '5px' },
  editBtn: { background: colors.amber, color: 'white' },
  deleteBtn: { background: colors.red, color: 'white' },
  saveBtn: { background: colors.parGreen, color: 'white' },
  cancelBtn: { background: colors.surface2, color: colors.text },
  emptyState: { textAlign: 'center', padding: '40px', color: colors.textMuted, fontSize: '16px', background: colors.surface, borderRadius: '10px', border: `1px solid ${colors.border}` }
};

export default ManageCarList;
